import React, { lazy } from 'react';
import PropTypes from 'prop-types';
import { css } from '@clearscore/helpers.injectors';
import LazyComponent from '@clearscore/helpers.lazy-component';
import getEnv from '@clearscore/helpers.envs';
import Loader from '@clearscore/rainbow.loader';

const currentStore = {};
const EXPERIMENT_GROUP = 'esm-rollout:test';

// Placeholder, needed for tests
let getLazy = () => () => null;
if (process.env.MODULE === 'esm') {
    getLazy = (path) => lazy(() => import(path).then((module) => ({ default: module.Component })));
}

const LazyLoadComponent = ({ component }) => {
    const { vertical, component: componentName, parentProps, routeProps, computedMatch, esmodules } = component;
    const isEsmRollout = routeProps?.segmentation?.groups?.find((segment) => segment === EXPERIMENT_GROUP);
    const assetPath = vertical ?? componentName;

    // While we are running AB Testing on Core for ESM/CJS we need a way to force an app (e.g. Nova)
    // to serve ESM verticals wether the user is in the esm segment or not, we are doing this by setting
    // env var in flux
    const isAppEsm = getEnv('IS_APP_ESM');

    const isEsmVertical = process.env.MODULE === 'esm' && (isEsmRollout || isAppEsm) && esmodules;
    const isUmdVertical = process.env.MODULE === 'umd' && esmodules;

    // we need to load the styles which are named like `index.css` because of naming clashes
    // for both modules and nomodules
    // while we are rending esm and cjs together we need to check that the asset path is esm
    if (isEsmVertical) {
        css(`${assetPath.slice(0, assetPath.indexOf('index'))}index.css`);
    }
    // while we are rending esm and cjs together we need to check that the asset path is esm
    if (isEsmVertical) {
        const Lazy = getLazy(assetPath.replace('index.js', 'index.mjs'));
        return (
            <React.Suspense fallback={<Loader isFullPage theme={Loader.themes.LIGHT} />}>
                <Lazy
                    {...parentProps}
                    {...routeProps}
                    match={computedMatch}
                    currentStore={currentStore}
                    routeConfig={component}
                />
            </React.Suspense>
        );
    }

    if (isUmdVertical) {
        return (
            <LazyComponent
                component={{
                    ...component,
                    name: component.name.split('.').join('--'),
                }}
            />
        );
    }

    return <LazyComponent component={component} />;
};

LazyLoadComponent.propTypes = {
    component: PropTypes.shape({
        routeProps: PropTypes.object.isRequired,
        computedMatch: PropTypes.object.isRequired,
        parentProps: PropTypes.object.isRequired,
        name: PropTypes.string.isRequired,
        vertical: PropTypes.string,
        component: PropTypes.string,
        esmodules: PropTypes.bool,
    }).isRequired,
};

export default LazyLoadComponent;
